import { IconCalendarTime, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  BotonExportarExcel,
  Group,
  IconAction,
  Paper,
  PaginadorTabla,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import type { PagoProgramado } from "../services/calendario-pagos.api";
import { SemaforoPagoBadge } from "./SemaforoPago";

const FMT = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 });

/**
 * Vista de TABLA del calendario de pagos · columnas equivalentes al portal
 * anterior + semáforo explícito, export a Excel y reprogramación por fila.
 * PAGINADA en el servidor: `pagos` es la página visible; el export usa
 * `pagosExport` (el set completo del periodo, que ya trae la query del
 * calendario mensual · sin fetch extra).
 */
export function PagosTable({
  pagos,
  pagosExport,
  total,
  page,
  pageSize,
  onPageChange,
  onReprogramar,
}: {
  pagos: PagoProgramado[];
  /** Set completo del periodo (sin paginar) · alimenta el export a Excel. */
  pagosExport: PagoProgramado[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onReprogramar: (pago: PagoProgramado) => void;
}) {
  const navigate = useNavigate();

  return (
    <Paper>
      <Group justify="flex-end" className="p-2">
        <PaginadorTabla total={total} page={page} pageSize={pageSize} onPageChange={onPageChange} variante="superior" />
        <BotonExportarExcel
          archivo="calendario-pagos"
          hoja="Pagos"
          filas={pagosExport}
          columnas={[
            { header: "Folio", value: (p) => p.folio },
            { header: "Sociedad", value: (p) => p.organizacionNombre },
            { header: "Proveedor", value: (p) => p.proveedorRazonSocial },
            { header: "Núm. acreedor", value: (p) => p.proveedorNumero },
            { header: "Descripción", value: (p) => p.descripcion },
            { header: "Cuenta bancaria", value: (p) => p.cuentaBancaria },
            { header: "CLABE", value: (p) => p.clabe },
            { header: "CFDIs", value: (p) => p.cantidadCfdis },
            { header: "Importe", value: (p) => p.importe },
            { header: "Moneda", value: (p) => p.moneda },
            { header: "Documento contable", value: (p) => p.documentoContable },
            { header: "Fecha solicitud", value: (p) => p.fechaSolicitud },
            { header: "Fecha de pago", value: (p) => p.fechaPago },
            { header: "Días programación", value: (p) => p.diasProgramacion },
            { header: "Estado", value: (p) => p.estado },
          ]}
        />
      </Group>
      <div className="overflow-x-auto">
        <Table verticalSpacing="sm" minWidth={1100}>
          <Thead>
            <Tr>
              <Th>Folio</Th>
              <Th>Proveedor</Th>
              <Th>Descripción</Th>
              <Th>Cuenta bancaria</Th>
              <Th align="right">Importe</Th>
              <Th>Doc. contable</Th>
              <Th>Fecha solicitud</Th>
              <Th>Fecha de pago</Th>
              <Th align="center">Días</Th>
              <Th>Estado</Th>
              <Th align="center">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {pagos.length === 0 ? (
              <Tr>
                <Td colSpan={11}>
                  <Text size="sm" c="dimmed" ta="center" className="py-5">
                    No hay pagos programados con los filtros actuales.
                  </Text>
                </Td>
              </Tr>
            ) : (
              pagos.map((p) => (
                <Tr key={p.solicitudId}>
                  <Td>
                    <Text size="sm" fw={600} className="font-mono">
                      {p.folio}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {p.organizacionNombre ?? "—"}
                    </Text>
                  </Td>
                  <Td className="max-w-50">
                    <Text size="sm" className="line-clamp-1" title={p.proveedorRazonSocial ?? undefined}>
                      {p.proveedorRazonSocial ?? "—"}
                    </Text>
                    {p.proveedorNumero && (
                      <Text size="xs" c="dimmed">
                        acreedor {p.proveedorNumero}
                      </Text>
                    )}
                  </Td>
                  <Td className="max-w-55">
                    <Text size="sm" className="line-clamp-2" title={p.descripcion ?? undefined}>
                      {p.descripcion ?? "—"}
                    </Text>
                  </Td>
                  <Td>
                    <Text size="sm" className="font-mono">
                      {p.cuentaBancaria ?? "—"}
                    </Text>
                  </Td>
                  <Td align="right">
                    <Text size="sm" fw={600} className="whitespace-nowrap">
                      ${FMT.format(p.importe)} {p.moneda ?? ""}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {p.cantidadCfdis} CFDI{p.cantidadCfdis === 1 ? "" : "s"}
                    </Text>
                  </Td>
                  <Td className="max-w-40">
                    <Text size="xs" className="line-clamp-1 font-mono" title={p.documentoContable ?? undefined}>
                      {p.documentoContable ?? "—"}
                    </Text>
                  </Td>
                  <Td>
                    <Text size="sm">{dayjs(p.fechaSolicitud).format("DD/MM/YYYY")}</Text>
                  </Td>
                  <Td>
                    <Text size="sm" fw={600}>
                      {dayjs(p.fechaPago).format("DD/MM/YYYY")}
                    </Text>
                  </Td>
                  <Td align="center">
                    <Text size="sm">{p.diasProgramacion}</Text>
                  </Td>
                  <Td>
                    <SemaforoPagoBadge estado={p.estado} />
                  </Td>
                  <Td>
                    <Group gap={4} justify="center" className="flex-nowrap">
                      <IconAction label="Reprogramar fecha de pago" size="sm" onClick={() => onReprogramar(p)}>
                        <IconCalendarTime size={16} stroke={1.7} />
                      </IconAction>
                      <IconAction
                        label="Ver solicitud"
                        size="sm"
                        onClick={() => void navigate({ to: "/solicitudes/$id", params: { id: p.solicitudId } })}
                      >
                        <IconExternalLink size={16} stroke={1.7} />
                      </IconAction>
                    </Group>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </div>
      <div className="p-2">
        <PaginadorTabla total={total} page={page} pageSize={pageSize} onPageChange={onPageChange} sustantivo="pagos" />
      </div>
    </Paper>
  );
}
